import { styled } from "@linaria/react"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { getRouteApi, Link, Navigate, useRouter } from "@tanstack/react-router"
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools"
import { observer } from "mobx-react-lite"
import { useEffect } from "react"

import { Button } from "@/components/Button/Button.tsx"
import { Card } from "@/components/Card/Card.tsx"
import { Centered } from "@/components/Centered/Centered.ts"
import { Horizontal, Spring, Vertical } from "@/components/Flex.ts"
import { FormField } from "@/components/FormField/FormField.tsx"
import { FormFieldStyles } from "@/components/FormField/FormFieldStyles.ts"
import { Heading2 } from "@/components/Heading/Heading.tsx"
import { FullWidthInput } from "@/components/Input/Input.tsx"
import { GlobalLoader } from "@/components/Loader/GlobalLoader.tsx"
import { useRootContext } from "@/hooks/useRootContext.ts"
import { ArrowLeftRegular, Icon20 } from "@/icons/Icons.ts"
import { getLogger } from "@/lib/logging.ts"
import { getStoredAuthToken } from "@/lib/oauth/AuthController.ts"
import { MatterLogoAndName } from "@/routes/auth/MatterLogoAndName.tsx"
import { PageBackgroundGradient } from "@/routes/auth/PageBackgroundGradient.ts"
import { PageBackground, PageContainer, PageContent } from "@/routes/auth/PageStyles.ts"
import { Navigator } from "@/routes/Navigator.ts"
import { ellipsize, smallerFontSize } from "@/styles/textStyles.ts"

import type { InvitationViewModel } from "@/routes/invitations/InvitationViewModel.ts"
import type { FormEvent } from "react"

const log = getLogger("InvitationScene")

const InvitationCard = styled(Card)`
  max-width: 400px;
  padding: 24px;

  background: var(--background-color);
  box-shadow: 0 2px 20px 0 rgb(26 46 160 / 5%);
  border-radius: var(--border-radius-16);
`

const InvitationHeader = styled(Vertical)`
  align-items: flex-start;

  text-align: left;
`

const InvitationForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;

  width: 352px;
`

const StyledFormField = styled(FormField)`
  gap: 4px;

  ${FormFieldStyles.Label} {
    ${smallerFontSize};

    color: var(--secondary-text-color);
    font-weight: 400;
  }
`

const StyledInput = styled(FullWidthInput)`
  height: 48px;
  padding: 12px 16px;
  border-radius: var(--border-radius-20);
`

const ErrorMessage = styled.div`
  margin-top: 16px;
  text-align: center;
  color: var(--error-color);
`

const ButtonsContainer = styled(Horizontal)`
  justify-content: space-between;
  gap: 12px;
`

const TermsAndConditions = styled.div`
  max-width: 330px;
  margin-top: 16px;
  text-align: center;
  font-size: 13px;
  line-height: 18px;
  color: var(--secondary-text-color);
`

const TermsLink = styled.a`
  color: var(--secondary-text-color);
  text-decoration: underline;
`

const DescriptionText = styled.div`
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.0006em;
`

const PageTitle = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  line-height: 30px;
  letter-spacing: -0.0004em;
  text-align: center;
`

const OrgsCentered = styled(Centered)`
  padding-top: 0;
  padding-bottom: 16px;
`

const LogoContainer = styled.div`
  margin-bottom: 24px;
`

const InvitationContent = observer(({ viewModel, orgId }: { viewModel: InvitationViewModel, orgId: string }) => {
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    viewModel.acceptInvitation().catch(error => {
      log.error("Failed to accept invitation:", error)
    })
  }

  if (!viewModel.invitationValid.get()) {
    return (
      <OrgsCentered>
        <InvitationCard>
          <InvitationHeader gap="16px">
            <Heading2>
              {viewModel.invitationExpired.get() ? "Invitation Expired" : "Invalid Invitation"}
            </Heading2>
            <div>
              {viewModel.errorMessage.get() || (
                viewModel.invitationExpired.get()
                  ? "This invitation link has expired."
                  : "This invitation link is not valid."
              )}
            </div>
          </InvitationHeader>
        </InvitationCard>
      </OrgsCentered>
    )
  }

  return (
    <OrgsCentered>
      <Spring />
      <LogoContainer>
        <MatterLogoAndName />
      </LogoContainer>
      <InvitationCard>
        <Vertical gap="16px">
          <InvitationForm onSubmit={handleSubmit}>
            <Vertical gap="16px">
              <PageTitle>Join {viewModel.orgName.get() || orgId}</PageTitle>
              <DescriptionText>
                Please fill in your details below to join the organization
              </DescriptionText>

              <StyledFormField label="First name">
                <StyledInput
                  value={viewModel.firstName.get()}
                  onChange={(e) => viewModel.firstName.set(e.target.value)}
                  placeholder="Enter your first name"
                  required
                  autoFocus
                />
              </StyledFormField>

              <StyledFormField label="Last name">
                <StyledInput
                  value={viewModel.lastName.get()}
                  onChange={(e) => viewModel.lastName.set(e.target.value)}
                  placeholder="Enter your last name"
                  required
                />
              </StyledFormField>

              <StyledFormField label="Username">
                <StyledInput
                  value={viewModel.username.get()}
                  onChange={(e) => {
                    viewModel.username.set(e.target.value)
                    viewModel.usernameManuallyTouched.set(true)
                  }}
                  placeholder="Enter your username"
                  required
                />
              </StyledFormField>

              {viewModel.errorMessage.get() && (
                <ErrorMessage>
                  {viewModel.errorMessage.get()}
                </ErrorMessage>
              )}
            </Vertical>

            <ButtonsContainer>
              <Link {...Navigator.home()} viewTransition>
                <Button size="large" kind="alternative" tabIndex={-1}>
                  <Horizontal gap="8px" alignItems="center">
                    <ArrowLeftRegular className={Icon20} />
                    Back
                  </Horizontal>
                </Button>
              </Link>

              <Button size="large" kind="primary" disabled={!viewModel.canSubmit}>
                {viewModel.submitting.get() ? ellipsize("Creating profile") : "Create profile"}
              </Button>
            </ButtonsContainer>
          </InvitationForm>
        </Vertical>
      </InvitationCard>
      <Spring />
      <TermsAndConditions>
        By creating a profile, you accept the <TermsLink href="https://www.jetbrains.com/legal/docs/terms/matter_eap/1.0/" target="_blank" rel="noopener noreferrer">Terms&nbsp;of&nbsp;Service</TermsLink> and acknowledge our Privacy&nbsp;Policy
      </TermsAndConditions>
    </OrgsCentered>
  )
})

const useInvitationRoute = () => getRouteApi("/~/invite")

export const InvitationScene = observer(() => {
  const route = useInvitationRoute()
  const { token, orgId } = route.useSearch()
  const { model: viewModel } = route.useLoaderData()

  useEffect(() => {
    return () => {
      viewModel.terminate()
    }
  }, [viewModel])

  const router = useRouter()
  const context = useRootContext()

  const hasAuthToken = getStoredAuthToken() !== null

  if (!hasAuthToken) {
    const currentUrl = router.buildLocation(Navigator.invite(token, orgId)).href
    localStorage.setItem("redirect", currentUrl)
    return <Navigate {...Navigator.login()} replace />
  }

  return (
    <>
      {context.useLoader && <GlobalLoader />}

      <PageContainer>
        <PageContent twoPaneLayout={false}>
          <InvitationContent viewModel={viewModel} orgId={orgId} />
        </PageContent>
      </PageContainer>

      <PageBackground background={PageBackgroundGradient.Orgs} />

      {window.parent === window && import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
      {window.parent === window && import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </>
  )
})